/**
 * useAppliedJobsMap — surfaces the user's job_applications history as a fast
 * lookup map so list views (AgentJobsPanel, Jobs tab) can show an "Applied" /
 * "Optimized Earlier" badge without re-fetching per card.
 *
 * Two lookup paths are exposed because external job IDs are non-UUID strings
 * (LinkedIn/Naukri/Peerlist) and won't survive the uuid-typed `job_id` column —
 * so we also key by `${normalizedTitle}|${normalizedCompany}` as a fallback.
 *
 * Subscribes to postgres_changes so the badge updates immediately after the
 * agent flow saves a new artifact (no manual refresh needed).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { getCurrentUser } from '../../../utils/authUtils';
import type { JobApplication } from '../../../services/applicationTrackingService';

export interface AppliedJobInfo {
  id: string;
  status: JobApplication['application_status'];
  hasResume: boolean;
  hasCoverLetter: boolean;
  hasApplicationNote: boolean;
  matchScore: number | null;
  lastUpdated: string;
  /** Saved optimized resume markdown (for re-opening in canvas). */
  resumeMarkdown: string | null;
  /** Saved cover letter snippet. */
  coverLetter: string | null;
  /** Saved application note. */
  applicationNote: string | null;
  /** Portal URL saved when the user last clicked Apply. */
  jobUrl: string | null;
}

function normalize(value: string | null | undefined): string {
  return (value || '').toLowerCase().trim().replace(/\s+/g, ' ');
}

function buildKey(title: string | null | undefined, company: string | null | undefined): string {
  return `${normalize(title)}|${normalize(company)}`;
}

export function useAppliedJobsMap() {
  const [byJobId, setByJobId] = useState<Map<string, AppliedJobInfo>>(new Map());
  const [byTitleCompany, setByTitleCompany] = useState<Map<string, AppliedJobInfo>>(new Map());
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const user = await getCurrentUser();
      if (!user) {
        setByJobId(new Map());
        setByTitleCompany(new Map());
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('job_applications')
        .select(
          'id, job_id, job_title, company_name, application_status, application_notes, ' +
            'resume_version, cover_letter_snippet, application_snippet, confidence_score, last_updated',
        )
        .eq('user_id', user.id)
        .order('last_updated', { ascending: false });

      if (error) {
        console.warn('[useAppliedJobsMap] fetch failed:', error.message);
        setIsLoading(false);
        return;
      }

      const idMap = new Map<string, AppliedJobInfo>();
      const tcMap = new Map<string, AppliedJobInfo>();

      for (const row of data ?? []) {
        const info: AppliedJobInfo = {
          id: row.id,
          status: row.application_status as JobApplication['application_status'],
          hasResume: Boolean(row.resume_version),
          hasCoverLetter: Boolean(row.cover_letter_snippet),
          hasApplicationNote: Boolean(row.application_snippet),
          matchScore: typeof row.confidence_score === 'number' ? row.confidence_score : null,
          lastUpdated: row.last_updated as string,
          resumeMarkdown: (row.resume_version as string | null) ?? null,
          coverLetter: (row.cover_letter_snippet as string | null) ?? null,
          applicationNote: (row.application_snippet as string | null) ?? null,
          jobUrl: (row.job_url as string | null) ?? null,
        };

        if (row.job_id) idMap.set(String(row.job_id), info);

        // Also extract external job id from the notes field, if persisted there
        if (row.application_notes && typeof row.application_notes === 'string') {
          const m = row.application_notes.match(/\[external_job_id:\s*([^\]]+)\]/);
          if (m && m[1]) idMap.set(m[1].trim(), info);
        }

        if (row.job_title || row.company_name) {
          tcMap.set(buildKey(row.job_title, row.company_name), info);
        }
      }

      setByJobId(idMap);
      setByTitleCompany(tcMap);
      setIsLoading(false);
    } catch (err) {
      console.warn('[useAppliedJobsMap] unexpected:', err);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Live-refresh when the agent flow saves new artifacts
  useEffect(() => {
    let cancelled = false;
    let channelRef: ReturnType<typeof supabase.channel> | null = null;
    (async () => {
      const user = await getCurrentUser();
      if (!user || cancelled) return;
      channelRef = supabase
        .channel(`applied-jobs-${user.id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'job_applications',
            filter: `user_id=eq.${user.id}`,
          },
          () => { void refresh(); },
        )
        .subscribe();
    })();
    return () => {
      cancelled = true;
      if (channelRef) void supabase.removeChannel(channelRef);
    };
  }, [refresh]);

  const lookup = useCallback(
    (jobId: string | null | undefined, jobTitle?: string | null, company?: string | null): AppliedJobInfo | null => {
      if (jobId && byJobId.has(String(jobId))) return byJobId.get(String(jobId))!;
      if (jobTitle || company) {
        const key = buildKey(jobTitle, company);
        if (byTitleCompany.has(key)) return byTitleCompany.get(key)!;
      }
      return null;
    },
    [byJobId, byTitleCompany],
  );

  return useMemo(
    () => ({ byJobId, byTitleCompany, isLoading, refresh, lookup }),
    [byJobId, byTitleCompany, isLoading, refresh, lookup],
  );
}

export default useAppliedJobsMap;
